import sortBy from 'lodash/sortBy';
import { getRotatePosition } from '@/utils/collision-kit';
import { convertLayerInfoToKeyAxis, convertLayerInfoToPoints, getLayerInfoWithRotate } from '@/utils/layer-kit';
import type { ILayerInfo, ITransform } from '@/version-types';
import type { SmartGuideLine } from '../../types';

function getRotatedPoints(layer: ITransform, layerRect: ILayerInfo, direction?: 'v' | 'h') {
    const points = getRotatePosition(layer);

    const { left, top, width, height, id } = layerRect;
    const centerX = Math.round(width / 2) + left;
    const centerY = Math.round(height / 2) + top;
    if (layer.isStage) {
        if (direction === 'v') {
            points.push({
                id,
                x: centerX,
                y: top,
            });
            points.push({
                id,
                x: centerX,
                y: height + top,
            });
        } else if (direction === 'h') {
            points.push({
                id,
                x: left,
                y: centerY,
            });
            points.push({
                id,
                x: width + left,
                y: centerY,
            });
        }
    } else {
        // 加入中心点
        points.push({
            id,
            x: centerX,
            y: centerY,
        });
    }

    return points;
}

type NearestLine = {
    /** 目标图层 ID；舞台等无业务图层来源时允许缺失。 */
    id?: string;
    /** 当前图层到候选线的距离，单位为编辑器逻辑坐标。 */
    distance: number;
    /** 吸附后当前图层应处于的坐标，单位为编辑器逻辑坐标。 */
    position: number;
};

/** 一次吸附准备阶段中的候选对齐线索引。 */
class LineList {
    /** 参与最近线计算的旋转包围盒。 */
    private data: ILayerInfo[];
    /** 按 ID 保存原始变换信息，用于生成渲染线段。 */
    private layerMap: Record<string, ITransform>;

    constructor() {
        this.data = [];
        this.layerMap = {};
    }

    get length() {
        return this.data.length;
    }

    push(layer: ITransform) {
        const info = getLayerInfoWithRotate(layer);
        this.data.push(info);
        this.layerMap[info.id] = layer;
    }

    getNearest(layer: ITransform) {
        const layerRect = getLayerInfoWithRotate(layer);
        const infoA = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);
            [infoA.lt, infoA.tc, infoA.rt].forEach((a, index) => {
                [infoB.lt, infoB.tc, infoB.rt].forEach((b) => {
                    vertical.push({
                        id: item.id,
                        // 2 条线的间隔
                        distance: Math.floor(a.x - b.x),
                        // 目标线的 x 坐标
                        position: Math.floor(b.x - ~~(index === 0 ? 0 : layerRect.width / (3 - index))),
                    });
                });
            });
            [infoA.lt, infoA.lc, infoA.lb].forEach((a, index) => {
                [infoB.lt, infoB.lc, infoB.lb].forEach((b) => {
                    horizontal.push({
                        id: item.id,
                        // 2 条线的间隔
                        distance: Math.floor(a.y - b.y),
                        // 目标线的 y 坐标
                        position: Math.floor(b.y - ~~(index === 0 ? 0 : layerRect.height / (3 - index))),
                    });
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getNearestLineForResize(layer: ITransform, directionVector: { x: number; y: number }) {
        const layerRect = getLayerInfoWithRotate(layer);
        const layerPoints = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        const { x: directionX, y: directionY } = directionVector;
        const xPoint = directionX === 0 ? Infinity : directionX > 0 ? layerPoints.rt.x : layerPoints.lt.x;
        const yPoint = directionY === 0 ? Infinity : directionY > 0 ? layerPoints.rb.y : layerPoints.rt.y;
        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);

            // resize 的时候可以不用考虑中心点
            // [infoB.lt, infoB.tc, infoB.rt]
            [infoB.lt, infoB.rt].forEach((b) => {
                vertical.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: xPoint - b.x,
                    // 目标线的 x 坐标
                    position: b.x,
                });
            });
            // [infoB.lt, infoB.lc, infoB.lb]
            [infoB.lt, infoB.lb].forEach((b) => {
                horizontal.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: yPoint - b.y,
                    // 目标线的 y 坐标
                    position: b.y,
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getNearestLineForScale(layer: ITransform, directionVector: { x: number; y: number }) {
        const layerRect = getLayerInfoWithRotate(layer);
        const layerPoints = convertLayerInfoToPoints(layerRect);
        const vertical: NearestLine[] = [];
        const horizontal: NearestLine[] = [];

        const { x: directionX, y: directionY } = directionVector;
        const xPoint = directionX === 0 ? Infinity : directionX > 0 ? layerPoints.rt.x : layerPoints.lt.x;
        const yPoint = directionY === 0 ? Infinity : directionY > 0 ? layerPoints.rb.y : layerPoints.rt.y;
        this.data.forEach((item) => {
            const infoB = convertLayerInfoToPoints(item);

            // resize 的时候可以不用考虑中心点
            // [infoB.lt, infoB.tc, infoB.rt]
            [infoB.lt, infoB.rt].forEach((b) => {
                vertical.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: xPoint - b.x,
                    // 目标线的 x 坐标
                    position: b.x,
                });
            });
            // [infoB.lt, infoB.lc, infoB.lb]
            [infoB.lt, infoB.lb].forEach((b) => {
                horizontal.push({
                    id: item.id,
                    // 2 条线的间隔
                    distance: yPoint - b.y,
                    // 目标线的 y 坐标
                    position: b.y,
                });
            });
        });

        if (vertical.length && horizontal.length) {
            return {
                // 最近的垂直线
                vertical: sortBy(vertical, (p) => Math.abs(p.distance))[0],
                // 最近的水平线
                horizontal: sortBy(horizontal, (p) => Math.abs(p.distance))[0],
            };
        }

        return null;
    }

    getLines(currentLayer: ITransform, ignoreCenter: boolean = false) {
        const currentLayerRect = getLayerInfoWithRotate(currentLayer);
        const currentLayerPoints = convertLayerInfoToKeyAxis(currentLayerRect, ignoreCenter);
        const currentLayerRotatedPoints = getRotatedPoints(currentLayer, currentLayerRect);

        const res: SmartGuideLine[] = [];

        this.data.forEach((layerRect) => {
            const layerPoints = convertLayerInfoToKeyAxis(layerRect, ignoreCenter);
            layerPoints.x.forEach((x) => {
                if (currentLayerPoints.x.includes(x)) {
                    const layer = this.layerMap[layerRect.id];
                    const layerRotatedPoints = getRotatedPoints(layer, layerRect, 'v');
                    const points = [...layerRotatedPoints, ...currentLayerRotatedPoints]
                        .filter((p) => Math.abs(p.x - x) < 1.5)
                        .sort((a, b) => a.y - b.y);
                    res.push({
                        id: layerRect.id,
                        isHorizontal: false,
                        points: points.map((p) => ({ x, y: p.y })),
                    });
                }
            });
            layerPoints.y.forEach((y) => {
                if (currentLayerPoints.y.includes(y)) {
                    const layer = this.layerMap[layerRect.id];
                    const layerRotatedPoints = getRotatedPoints(layer, layerRect, 'h');
                    const points = [...layerRotatedPoints, ...currentLayerRotatedPoints]
                        .filter((p) => Math.abs(p.y - y) < 1.5)
                        .sort((a, b) => a.x - b.x);
                    res.push({
                        id: layerRect.id,
                        isHorizontal: true,
                        points: points.map((p) => ({ x: p.x, y })),
                    });
                }
            });
        });
        return res;
    }
}

export default LineList;
